import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import QueueBar from "@/components/QueueBar";
import { markDone } from "@/lib/practiceQueue";
import DiffView from "@/components/DiffView";
import StatBox from "@/components/StatBox";
import CertificateBadge from "@/components/CertificateBadge";
import PersonalBestBadge from "@/components/PersonalBestBadge";
import KeyboardSoundToggle from "@/components/KeyboardSoundToggle";
import AdaptivePicks from "@/components/AdaptivePicks";
import { keyClickHandler } from "@/lib/keyboardSound";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { showBadgeUnlocks, showMistakesAddedToast } from "@/lib/badgeToast";
import { RotateCcw, ArrowLeft, Send, ZoomIn, X, Clock } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function fmtMMSS(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

/** Fullscreen image lightbox with pinch/scroll zoom (mouse wheel) and drag pan. */
function ImageLightbox({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setScale((s) => Math.max(1, Math.min(5, s + delta)));
  };
  const onMouseDown = (e) => { dragRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y }; };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPos({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const onMouseUp = () => { dragRef.current = null; };
  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
      onWheel={onWheel}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      data-testid="pitman-lightbox"
    >
      <button onClick={onClose} className="absolute top-4 right-4 bg-white/10 hover:bg-card/20 text-white px-3 py-2 text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-2" data-testid="pitman-lightbox-close">
        <X className="w-4 h-4" /> Close
      </button>
      <div className="absolute top-4 left-4 bg-card/10 text-white px-3 py-2 text-xs uppercase tracking-[0.2em] font-bold flex items-center gap-3">
        <span>Zoom {Math.round(scale * 100)}%</span>
        <button onClick={() => setScale((s) => Math.min(5, s + 0.25))} className="hover:underline">+</button>
        <button onClick={() => setScale((s) => Math.max(1, s - 0.25))} className="hover:underline">−</button>
        <button onClick={reset} className="hover:underline">Reset</button>
      </div>
      <img
        src={src}
        alt={alt}
        onMouseDown={onMouseDown}
        draggable="false"
        className="max-h-[90vh] max-w-[95vw] select-none"
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: dragRef.current ? "none" : "transform 60ms linear",
          cursor: dragRef.current ? "grabbing" : "grab",
          imageRendering: scale > 1.5 ? "pixelated" : "auto",
          filter: "contrast(1.1)",
        }}
      />
    </div>
  );
}

export default function PitmanPractice() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [typed, setTyped] = useState("");
  const [start, setStart] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [lightbox, setLightbox] = useState(null); // {src, alt}
  const timerRef = useRef(null);
  const autoSubmittedRef = useRef(false);
  const onKey = useMemo(() => keyClickHandler(), []);

  useEffect(() => {
    api.get(`/pitman-exercises/${id}`).then((r) => {
      setItem(r.data);
      setRemaining(r.data.time_limit_seconds || 900);
    }).catch(() => toast.error("Not found"));
  }, [id]);

  const submit = useCallback(async (opts = {}) => {
    if (submitting) return;
    const auto = !!opts.auto;
    if (!auto && !typed.trim()) { toast.error("Type something first"); return; }
    setSubmitting(true);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try {
      const seconds = start ? (Date.now() - start) / 1000 : (item?.time_limit_seconds || 900);
      const { data } = await api.post("/attempts", {
        module: "pitman", item_id: id, typed_text: typed || "-", time_seconds: seconds,
      });
      setResult(data);
      markDone("pitman", id);
      toast.success(auto ? `Time up! Auto-submitted — ${data.accuracy_percent}% accuracy` : `Accuracy: ${data.accuracy_percent}%`);
      showBadgeUnlocks(data.new_badges);
      showMistakesAddedToast(data.mistakes_added);
    } catch { toast.error("Submission failed"); } finally { setSubmitting(false); }
  }, [submitting, typed, start, id, item]);

  // Countdown after student starts typing OR after 10s of page load (whichever first)
  useEffect(() => {
    if (!item || result) return;
    // Kick off timer once we know time limit; timer is display-only until student types.
    if (timerRef.current) return;
    timerRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          // Time up — auto submit once
          if (!autoSubmittedRef.current) {
            autoSubmittedRef.current = true;
            submit({ auto: true });
          }
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  }, [item, result, submit]);

  const handleType = (e) => {
    setTyped(e.target.value);
    if (!start) setStart(Date.now());
  };

  const reset = () => {
    setTyped(""); setStart(null); setResult(null);
    setRemaining(item?.time_limit_seconds || 900);
    autoSubmittedRef.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const images = item?.images || [];
  const timeUp = remaining <= 0 && !result;
  const timerTone = remaining <= 60 ? "text-red-600" : remaining <= 180 ? "text-amber-600" : "text-brand-gold";

  return (
    <div className="min-h-screen bg-card flex flex-col">
      <Navbar />
      <QueueBar module="pitman" currentId={id} hrefFor={(nid) => `/pitman/${nid}`} />
      <main className="max-w-7xl mx-auto w-full px-6 py-8 flex-1 flex flex-col">
        <Link to="/pitman" className="text-xs uppercase tracking-[0.3em] font-bold text-muted-foreground hover:text-brand-gold" data-testid="back-to-pitman">
          <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Back to Exercises
        </Link>

        {!item ? <div className="mt-8 font-mono text-muted-foreground">Loading…</div> : (
          <>
            <div className="mt-4 mb-4 flex items-start justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-brand-gold">{item.chapter} · Pitman Shorthand</div>
                <h1 className="text-3xl lg:text-4xl font-black tracking-tight mt-2" data-testid="pitman-title">{item.title}</h1>
              </div>
              {!result && (
                <div className={`border-2 border-current ${timerTone} px-4 py-2 flex items-center gap-2 font-mono font-bold text-xl`} data-testid="pitman-timer">
                  <Clock className="w-5 h-5" />
                  <span data-testid="pitman-timer-value">{fmtMMSS(remaining)}</span>
                </div>
              )}
            </div>

            {/* Split screen */}
            <div className="grid md:grid-cols-2 border border-border flex-1 min-h-[360px]">
              {/* Left: Shorthand images (or fallback content) */}
              <div className="p-4 border-b md:border-b-0 md:border-r border-border bg-secondary/60 overflow-y-auto max-h-[70vh]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3 flex items-center justify-between">
                  <span>Shorthand ({images.length || 0} page{images.length !== 1 ? "s" : ""})</span>
                  {images.length > 0 && <span className="text-[9px] normal-case tracking-normal text-muted-foreground/70">Click any image to zoom</span>}
                </div>

                {images.length === 0 ? (
                  <div className="font-mono text-sm text-muted-foreground whitespace-pre-wrap" data-testid="pitman-source-text">
                    {item.content}
                    <div className="mt-4 text-xs text-muted-foreground/70 italic">(No shorthand image uploaded — admin can add one from the panel.)</div>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="pitman-images">
                    {images.map((url, i) => (
                      <button
                        type="button"
                        key={url}
                        onClick={() => setLightbox({ src: `${BACKEND}${url}`, alt: `Page ${i + 1}` })}
                        className="w-full border border-border overflow-hidden bg-white hover:border-brand-gold/40 hover:shadow-lg transition-all relative group rounded-lg"
                        data-testid={`pitman-image-${i}`}
                      >
                        <img
                          src={`${BACKEND}${url}`}
                          alt={`Page ${i + 1}`}
                          className="w-full h-auto block"
                          loading="lazy"
                          decoding="async"
                          style={{ imageRendering: "auto", maxWidth: "100%", filter: "contrast(1.06)" }}
                        />
                        <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 flex items-center gap-1 text-[10px] uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity rounded">
                          <ZoomIn className="w-3 h-3" /> Zoom
                        </div>
                        <div className="absolute bottom-2 left-2 bg-white/95 text-black px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded">Page {i + 1}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Right: Typing */}
              <div className="p-4 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">Your transcription</div>
                  <KeyboardSoundToggle />
                </div>
                <Textarea
                  value={typed}
                  onChange={handleType}
                  onKeyDown={onKey}
                  disabled={submitting || !!result || timeUp}
                  placeholder="Read the shorthand and type the transcription here…"
                  className="flex-1 min-h-[300px] rounded-lg border-2 border-brand-gold/30 bg-white text-[#0F172A] placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:border-brand-gold font-mono font-bold text-base leading-loose"
                  data-testid="pitman-textarea"
                />
                <div className="mt-2 text-[10px] font-mono text-muted-foreground flex justify-between">
                  <span>{typed.trim().split(/\s+/).filter(Boolean).length} words typed</span>
                  {timeUp && <span className="text-red-600 font-bold">Time up — auto submitted</span>}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-6">
              <Button onClick={() => submit()} disabled={submitting || !!result || timeUp} className="rounded-lg h-12 px-8 bg-brand-gold hover:bg-brand-gold/90 text-white text-xs uppercase tracking-[0.2em] font-bold" data-testid="pitman-submit-btn">
                <Send className="w-4 h-4 mr-2" /> {submitting ? "Checking…" : "Submit & Check"}
              </Button>
              <Button onClick={reset} variant="outline" className="rounded-lg h-12 px-6 border-border" data-testid="pitman-reset-btn">
                <RotateCcw className="w-4 h-4 mr-2" /> Reset
              </Button>
            </div>

            {result && (
              <section className="mt-12" data-testid="pitman-result">
                <h2 className="text-2xl font-black tracking-tight mb-6">Results</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <StatBox testId="pitman-result-accuracy" label="Accuracy" value={result.accuracy_percent} unit="%" tone={result.accuracy_percent >= 90 ? "good" : result.accuracy_percent >= 70 ? "warn" : "bad"} />
                  <StatBox testId="pitman-result-errors" label="Errors" value={result.error_percent} unit="%" tone="bad" />
                  <StatBox testId="pitman-result-wpm" label="WPM" value={result.wpm} unit="wpm" tone="accent" />
                  <StatBox testId="pitman-result-words" label="Words" value={`${result.correct}/${result.total_words}`} tone="default" />
                </div>
                <CertificateBadge attemptId={result.id} accuracy={result.accuracy_percent} wpm={result.wpm} />
                <PersonalBestBadge result={result} />
                <div className="mt-8">
                  <DiffView result={result.diff} />
                </div>
                <div className="mt-10">
                  <AdaptivePicks module="pitman" limit={3} title="Try next" />
                </div>
              </section>
            )}

            {lightbox && (
              <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
            )}
          </>
        )}
      </main>
    </div>
  );
}
